// C:\Users\User\Desktop\Bresco\bresco-franquicies-frontend\src\components\Franquicias\FranquiciasCompGrid.js
import ColumnFilter from "components/ui/Table/Filters/ColumnFilter";
import Table from "components/ui/Table/Table";
import { useModal } from "contexts/ModalContext";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { ImSpinner9 } from "react-icons/im";
import { LuEye, LuSquarePen, LuTrash2 } from "react-icons/lu";
import { Link } from "wouter";

/**
 * Intenta extraer token de usuario sin conocer exactamente tu estructura.
 */
const getAuthToken = (user) => {
  if (!user) return null;
  return user.token || user.accessToken || user.access_token || user.jwt || user.jwt_token || null;
};

export default function FranquiciasCompGrid({
  user,
  data,
  handleRefresh,
  handleDelete,
  handleAnalyzeExcel
}) {
  const { openModal } = useModal();
  const [tableData, setTableData] = useState(null);

  const token = getAuthToken(user);

  /**
   * NUEVO: Auto-refresh 1 vez cuando el token esté listo.
   */
  const didInitialRefresh = useRef(false);
  useEffect(() => {
    if (!didInitialRefresh.current && token) {
      didInitialRefresh.current = true;
      handleRefresh();
    }
  }, [token, handleRefresh]);

  const columns = useMemo(
    () => [
      {
        title: "Filtrar por -Código-",
        Header: "Código",
        accessor: "Franquicias_Codigo",
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true
      },
      {
        title: "Filtrar por -Código 3 dígitos-",
        Header: "Cod. 3D",
        accessor: "Franquicias_Codigo3",
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true
      },
      {
        title: "Filtrar por -Nombre-",
        Header: "Nombre",
        accessor: "Franquicias_Nombre",
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true
      },
      {
        title: "Filtrar por -Propiedad-",
        Header: "Propiedad",
        accessor: "Franquicias_Propiedad",
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true
      },
      {
        title: "Filtrar por -CIF/NIF-",
        Header: "CIF/NIF",
        accessor: "Franquicias_CifNif",
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true
      },
      {
        title: "Filtrar por -IBAN-",
        Header: "IBAN",
        accessor: "Franquicias_Iban",
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true
      },
      {
        title: "Filtrar por -Email-",
        Header: "Email",
        accessor: "Franquicias_Email",
        Filter: ColumnFilter,
        showInPDF: true,
        showInExcel: true
      },

      // Acciones
      {
        title: "Acciones",
        Header: "",
        accessor: "Franquicias_Actions",
        disableSortBy: true,
        Cell: (value) => {
          const codigo = value?.cell?.row?.original?.Franquicias_Codigo;

          return (
            <div className="float-right px-4 py-4 text-sm whitespace-nowrap">
              <div className="flex items-center gap-x-6">
                <Link
                  title={user.role_id === 1 ? "Editar registro" : "Ver registro"}
                  href={`/franquicias/${codigo}`}
                  className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
                >
                  {user.role_id === 1 || user.role_id === 2 ? <LuSquarePen size={20} /> : <LuEye size={20} />}
                </Link>

                {user.role_id === 1 || user.role_id === 2 ? (
                  <button
                    title="Eliminar registro"
                    type="button"
                    className="text-gray-500 transition-colors duration-200 dark:hover:text-red-500 dark:text-gray-300 hover:text-red-500 focus:outline-none"
                    onClick={() =>
                      openModal("DeleteModal", {
                        itemName: value?.cell?.row?.original?.Franquicias_Nombre || codigo,
                        itemId: codigo,
                        handleDelete
                      })
                    }
                  >
                    <LuTrash2 size={20} />
                  </button>
                ) : null}
              </div>
            </div>
          );
        },
        showInPDF: false,
        showInExcel: false
      }
    ],
    [openModal, user, handleDelete]
  );

  /**
   * NUEVO: Diferenciar loading (data null/undefined) vs respuesta vacía.
   */
  useEffect(() => {
    if (data === undefined || data === null) {
      setTableData(null);
      return;
    }

    if (Array.isArray(data)) {
      const items = data.map((item) => ({
        Franquicias_Codigo: item.codigo_franquicia,
        Franquicias_Codigo3: item.codigo_fq_3dig,
        Franquicias_Nombre: item.nombre,
        Franquicias_Propiedad: item.propiedad_franquicia,
        Franquicias_CifNif: item.cif_nif,
        Franquicias_Iban: item.iban,
        Franquicias_Email: item.email
      }));

      setTableData(items);
    } else {
      setTableData([]);
    }
  }, [data]);

  return tableData ? (
    <Table
      tableName={"Franquicias"}
      columns={columns}
      createNewItemURL={"/franquicias/new"}
      data={tableData}
      defaultSort={{ id: "Franquicias_Codigo", desc: false }}
      hiddenColumns={[]}
      allowImportExcel={true}
      uploadExcelURL={"franquicias/import/replace"}
      handleRefresh={handleRefresh}
      handleAnalyzeExcel={handleAnalyzeExcel}
    />
  ) : (
    <div className="flex items-center content-center justify-center w-full h-full">
      <p
        className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700"
        title="Cargando..."
      >
        <ImSpinner9 size={70} className="animate-spin" />
      </p>
    </div>
  );
}

FranquiciasCompGrid.propTypes = {
  user: PropTypes.object,
  data: PropTypes.array,
  handleRefresh: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleAnalyzeExcel: PropTypes.func.isRequired
};
